<template>
  <div class="app-container">
    <div v-if="platformNotifications.length" class="notifications-container">
      <PlatformNotifications />
    </div>
    <div class="router-container">
      <router-view />
    </div>
  </div>

  <!-- Global Notifications Panel -->
  <div v-if="isNotificationsModalOpened" class="global-notifications-panel">
    <NotificationsPanelDialog />
  </div>

  <!-- Global Toast Messages -->
  <div class="global-toasts">
    <BbToastMessage
      v-for="(toast, index) in toasts"
      :key="index"
      :modelValue="!!toast"
      :title="toast.title"
      duration="4000"
      location="top centre"
      @update:modelValue="clearCurrentToast(toast.notification.externalId)"
    />
  </div>
</template>

<script>
import { onMounted, watch, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAuth0 } from '@auth0/auth0-vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { CURRENT_DIALOG } from '~/constants/conversationModels';
import { Routes } from '~/constants/routes';
import { GetDownloadInProgressCount } from '~/constants/getters';

import useDetectOS from '~/composables/useDetectOS';
import usePlatformNotifications from '~/composables/usePlatformNotifications';
import useNotifications from '~/composables/useNotifications';
import useModals from '~/composables/useModals';
import PlatformNotifications from '~/components/conversation/platformNotifications/PlatformNotifications.vue';
import NotificationsPanelDialog from '~/components/conversation/notificationsPanel/NotificationsPanelDialog.vue';

export default {
  name: 'App',
  components: { PlatformNotifications, NotificationsPanelDialog },
  setup() {
    const auth0 = useAuth0();
    const { locale, setLocaleMessage } = useI18n();
    const store = useStore();
    const route = useRoute();
    const { platformNotifications, getPlatformNotifications } = usePlatformNotifications();
    const { toasts, removeToastById, removeAllToasts } = useNotifications();
    const { currentDocumentsDialog, setCurrentDocumentsDialog } = useModals();

    // Routes that have AccountControlPanels (don't show global notifications panel)
    const routesWithAccountControlPanels = [Routes.Conversation?.name, Routes.Authoring?.name].filter(Boolean);

    // Only show global notifications panel on routes without AccountControlPanels
    const isNotificationsModalOpened = computed(() => {
      const isOnRouteWithPanels = routesWithAccountControlPanels.includes(route.name);
      return !isOnRouteWithPanels && currentDocumentsDialog.value === CURRENT_DIALOG.notifications_panel;
    });

    // Track count of downloads in progress (not specific ID)
    const downloadInProgressCount = computed(() => store.getters[`documentStore/${GetDownloadInProgressCount}`]);

    const { isWindows } = useDetectOS();

    const selectedPage = ref(0);

    const selectedPages = [{ title: 'Assist' }, { title: 'Author' }];

    // this will be moved or refactored once the be is ready
    const loadLocalization = async () => {
      const selectedLocale = locale.value;

      try {
        const dbMessages = {};

        // Merge dynamic messages with default locale
        const defaultMessages = await import(`./locales/${selectedLocale}.json`);
        const mergedMessages = { ...defaultMessages.default, ...dbMessages };

        // Set the locale messages
        setLocaleMessage(selectedLocale, mergedMessages);
        locale.value = selectedLocale;
      } catch (error) {
        console.error('Failed to load messages:', error);
      }
    };

    const clearCurrentToast = (index) => {
      removeToastById(index);
    };

    onMounted(async () => {
      // add custom style for windows scrollbars
      if (isWindows()) {
        document.body.classList.add('custom-scrollbar-windows');
      }
      store.dispatch('auth0Store/setAuth', auth0);
      await loadLocalization();
    });

    watch(
      () => auth0,
      async (newValue) => {
        // logic to execute after DOM updates
        await store.dispatch('auth0Store/setAuth', newValue);

        if (newValue.isAuthenticated.value) {
          await getPlatformNotifications();
        }
      },
      { immediate: true, deep: true },
    );

    watch(downloadInProgressCount, (newVal, oldVal) => {
      // When count decreases, a download completed - open notifications panel if not already open
      if (oldVal > newVal && currentDocumentsDialog.value !== CURRENT_DIALOG.notifications_panel) {
        setCurrentDocumentsDialog(CURRENT_DIALOG.notifications_panel);
        // Clear pending download toasts when opening notifications panel
        removeAllToasts();
      }
    });

    return {
      selectedPages,
      selectedPage,
      platformNotifications,
      locale,
      toasts,
      clearCurrentToast,
      isNotificationsModalOpened,
    };
  },
};
</script>

<style lang="scss">
/* Custom scrollbar styling */
.custom-scrollbar-windows {
  ::-webkit-scrollbar {
    width: $bb-spacing-1; /* or any thin size you prefer */
    height: $bb-spacing-1;
  }

  ::-webkit-scrollbar-track {
    background: transparent; /* Light padding zones at ends */
  }

  ::-webkit-scrollbar-thumb {
    background: #888; /* Or any color you prefer */
    border-radius: $bb-spacing-3;
  }
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.notifications-container {
  flex: 0 0 auto;
}

.router-container {
  flex: 1 1 auto;
  overflow: auto;
}

.global-toasts {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.global-notifications-panel {
  position: fixed;
  top: 60px;
  right: 16px;
  z-index: 9998;
}
</style>
